'use client';
import React, { HTMLInputAutoCompleteAttribute, useState } from 'react';
import styled from 'styled-components';
import { UseFormRegisterReturn } from 'react-hook-form';
import ContentText from '../ContentText';
import Tag from '@/components/molecules/Tag';
import { BREAKPOINT_SP } from '@/util/const';
import GapWrapper from '@/components/product/GapWrapper';

export type InputTextProps = {
  className?: string;
  required?: boolean;
  register?: UseFormRegisterReturn<any>;
  label?: string;
  value?: string;
  initValue?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  errorLabel?: string;
  icon?: React.ReactNode;
  isError?: boolean;
  endIcon?: React.ReactNode;
  guide?: React.ReactNode;
  type?: string;
  onEnterPressed?: (value?: string) => void;
  maxLength?: number;
  onEscapePressed?: () => void;
  widthLength?: number;
  smallPadding?: boolean;
  autoComplate?: HTMLInputAutoCompleteAttribute;
  autoFocus?: boolean;
  prefix?: React.ReactNode;
  id?: string;
};

const InputText: React.FC<InputTextProps> = ({
  autoComplate,
  className,
  required,
  register,
  label,
  value,
  initValue,
  onChange,
  disabled,
  placeholder,
  errorLabel,
  icon,
  isError,
  endIcon,
  guide,
  type,
  onEnterPressed,
  maxLength,
  onEscapePressed,
  widthLength,
  smallPadding,
  autoFocus,
  prefix,
  id,
}) => {
  const [focus, setFocus] = useState(false);
  const [isComposing, setIsComposing] = useState(false);
  function handleComposition(event: { type: string }) {
    if (event.type === 'compositionstart') {
      setIsComposing(true);
    }
    if (event.type === 'compositionend') {
      setIsComposing(false);
    }
  }
  return (
    <InputTextAll
      className={className}
      width={widthLength ? widthLength : 0}
      id={id}
    >
      {label && (
        <ContentText type="label-l" $whitespace="nowrap">
          <LabelContainer>
            {label}
            {required && (
              <Tag theme="error" small>
                必須
              </Tag>
            )}
          </LabelContainer>
        </ContentText>
      )}
      {guide && (
        <ContentText type="article-m" color="#585757">
          {guide}
        </ContentText>
      )}
      <GapWrapper $row $gap={4} $align="center" $width="100%">
        {prefix && <PrefixWrapper>{prefix}</PrefixWrapper>}
        <InputWrapper
          className={`${
            disabled ? 'disabled' : errorLabel ? 'error' : 'normal'
          } ${focus ? 'focus' : ''}`}
          $smallPadding={smallPadding}
        >
          {icon && <InputTextIcon>{icon}</InputTextIcon>}
          <input
            autoFocus={autoFocus}
            autoComplete={autoComplate}
            {...register}
            type={type}
            placeholder={placeholder}
            onChange={(e) => {
              onChange && onChange(e.target.value);
              register?.onChange(e);
            }}
            defaultValue={initValue}
            value={value}
            disabled={disabled}
            onFocus={() => {
              setFocus(true);
            }}
            onBlur={(e) => {
              setFocus(false);
              register?.onBlur?.(e);
            }}
            onKeyDown={(e) => {
              if (!isComposing) {
                if (onEnterPressed && e.key == 'Enter') {
                  onEnterPressed(value);
                } else if (onEscapePressed && e.key == 'Escape') {
                  onEscapePressed();
                }
              }
            }}
            onCompositionStart={handleComposition}
            onCompositionEnd={handleComposition}
            maxLength={maxLength}
          ></input>
          {endIcon && <InputTextIcon>{endIcon}</InputTextIcon>}
        </InputWrapper>
      </GapWrapper>
      {errorLabel && <InputTextNoError>{errorLabel}</InputTextNoError>}
    </InputTextAll>
  );
};

export default InputText;

const LabelContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const InputTextAll = styled.div<{ width: number }>`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  div.focus {
    box-shadow: 0 0 0 2px var(--base-black, #18181b);
  }
  div.disabled {
    border: 1px solid var(--border-disabled, #949497);
    background: var(--color-light-gray, #d5d2d0);
  }
  div.error {
    border: 1.5px solid var(--error-main, #e13a62);
  }
  input {
    outline: none;
    ${({ width }) => {
      if (width != 0) {
        return `width: ` + width + `px;`;
      } else {
        return `width: 100%;`;
      }
    }}
    color: var(--base-black, #18181b);
    font-feature-settings: 'pwid' on;
    font-family: 'Noto Sans JP';
    font-size: 1rem;
    font-style: normal;
    font-weight: 400;
    line-height: 1.75;
    letter-spacing: 0.64px;
    border: 0;
  }
  input::placeholder {
    color: var(--color-placeholder);
  }
  input:disabled {
    background: var(--color-light-gray, #d5d2d0);
  }
`;

const InputWrapper = styled.div<{ $smallPadding?: boolean }>`
  border-radius: 4px;
  border: 1px solid var(--color-gray, #a0a0a0);
  background: var(--background-primary, #fff);
  display: flex;
  padding: ${({ $smallPadding }) => ($smallPadding ? '8px' : '12px')};
  width: 100%;
  align-items: center;
  align-self: stretch;
  gap: 8px;
  @media (max-width: ${BREAKPOINT_SP}) {
    padding: 8px;
  }
  /* Chrome, Safari, Edge, Opera */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  input[type='number'] {
    -moz-appearance: textfield;
  }
`;
export const InputTextNoError = styled.div<{ smallPadding?: boolean }>`
  display: flex;
  padding-left: ${({ smallPadding }) => (smallPadding ? '8px' : '12px')};
  align-items: flex-start;
  color: var(--error-main, #e13a62);
  font-feature-settings: 'pwid' on;
  font-family: 'Noto Sans JP';
  font-size: 0.75rem;
  font-style: normal;
  font-weight: 500;
  line-height: 1.75;
  letter-spacing: 0.15px;
`;
const InputTextIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 24px;
`;
const PrefixWrapper = styled.div`
  display: flex;
  align-items: center;
  font-size: 1rem;
  color: var(--base-black, #18181b);
  white-space: nowrap;
`;
